import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import JobsMap from '../schedule/JobsMap';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import { date } from '../../shared/formatters';

const STATUS_OPTIONS = [
  ['all', 'All statuses'],
  ['scheduled', 'Scheduled'],
  ['in_progress', 'In progress'],
  ['completed', 'Completed'],
  ['requested', 'Requested by client'],
  ['reschedule_requested', 'Reschedule requested'],
];

const ETA_LABEL = { on_way: 'On the way', delayed: 'Delayed', arrived: 'Arrived', completed: 'Completed' };
const ETA_CLASS = { on_way: 'status-blue', delayed: 'status-red', arrived: 'status-green', completed: 'status-gray' };

// Standalone admin map (update_tasks.md §7.3, Phase 0): reuses the
// Schedule JobsMap component (prop-driven, reads nothing from context)
// with search/engineer/status filters. Jobs without coordinates are
// counted and explained inside JobsMap, never silently dropped. Job
// editing stays in the Schedule (map popups navigate there).
export default function Mapping() {
  const { data } = useData();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [engineer, setEngineer] = useState('all'); // engineer_id
  const [status, setStatus] = useState('all');

  const clientName = (clientId) => data.clients.find((item) => item.id === clientId)?.name || 'Unknown client';

  const engineerName = (engineerId) => data.team.find((member) => member.id === engineerId)?.name
    || data.engineers.find((person) => person.id === engineerId)?.name || 'Unknown';

  const mapJobs = data.jobs.filter((job) => {
    const client = data.clients.find((item) => item.id === job.client_id);
    const haystack = `${job.title} ${client?.name || ''} ${job.engineer || ''}`.toLowerCase();
    if (query && !haystack.includes(query.toLowerCase())) return false;
    if (engineer !== 'all' && job.engineer_id !== engineer) return false;
    if (status !== 'all' && job.status !== status) return false;
    return true;
  });

  // ETA & location activity (update_tasks.md §7.3 Phase 1): latest ping per
  // engineer + a recent ETA-update timeline. Agency rules already grant full
  // read on eta_updates / engineer_locations; engineer location data is
  // sensitive (known-issues "engineer location privacy") — shown agency-only
  // within this admin screen.
  const latestLocations = useMemo(() => {
    const latest = {};
    (data.engineerLocations || []).forEach((loc) => {
      const prev = latest[loc.engineer_id];
      if (!prev || loc.timestamp > prev.timestamp) latest[loc.engineer_id] = loc;
    });
    return Object.values(latest).sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
  }, [data.engineerLocations]);

  const recentEta = useMemo(() => (data.etaUpdates || []).slice(0, 20), [data.etaUpdates]);

  const hasActivity = latestLocations.length > 0 || recentEta.length > 0;

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Engineer</span>
          <h1>Mapping</h1>
          <p className="field-hint">All jobs colour-coded by status — use the popups to jump into the Schedule to edit.</p>
        </div>
      </header>

      <div className="toolbar">
        <label className="search-input">
          <SafeIcon icon={Icons.Search} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search jobs, clients or engineers…" />
        </label>
        <select value={engineer} onChange={(event) => setEngineer(event.target.value)} className="engineer-filter" aria-label="Filter by engineer">
          <option value="all">All engineers</option>
          {data.engineers.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
        </select>
        <select value={status} onChange={(event) => setStatus(event.target.value)} className="engineer-filter" aria-label="Filter by status">
          {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>

      <JobsMap
        jobs={mapJobs}
        allJobs={data.jobs}
        clients={data.clients}
        clientName={clientName}
        onEditJob={() => navigate('/admin/schedule')}
      />

      {hasActivity && (
        <section className="portal-card">
          <div className="section-header">
            <h3>ETA &amp; location activity</h3>
          </div>
          {latestLocations.length > 0 && (
            <div className="detail-panel">
              <div className="section-header"><h4>Latest locations</h4></div>
              <ul className="expense-list">
                {latestLocations.map((loc) => (
                  <li className="expense-item" key={loc.id}>
                    <span className="expense-type">{engineerName(loc.engineer_id)}</span>
                    <span className="expense-note">
                      {loc.coordinates?.lat != null ? `${Number(loc.coordinates.lat).toFixed(5)}, ${Number(loc.coordinates.lng).toFixed(5)}` : loc.address || 'No coordinates'}
                      {loc.accuracy ? ` (±${loc.accuracy})` : ''}
                    </span>
                    <small className="field-hint">{date(loc.timestamp)}</small>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {recentEta.length > 0 && (
            <div className="detail-panel">
              <div className="section-header"><h4>Recent ETA updates</h4></div>
              <ul className="expense-list">
                {recentEta.map((eta) => (
                  <li className="expense-item" key={eta.id}>
                    <span className={`status-pill ${ETA_CLASS[eta.status] || 'status-gray'}`}>{ETA_LABEL[eta.status] || eta.status}</span>
                    <span className="expense-note">
                      {engineerName(eta.engineer_id)}
                      {eta.eta_minutes != null ? ` · ETA ${eta.eta_minutes} min` : ''}
                      {eta.message ? ` — ${eta.message}` : ''}
                    </span>
                    <small className="field-hint">{date(eta.created_at)}</small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
